/**
 * The bottom half of Admin → Ask ABHI: one card, four tabs.
 *
 *   Questions — every question, newest first, searchable
 *   People    — who uses it, with their ratings; click → their questions
 *   Review    — thumbs-downs, held-back answers, errors, thumbs-ups to look at
 *   Topics    — what people ask about (question types, states, products)
 *
 * Rows are list items (avatar · text · meta), not spreadsheet tables, and
 * every row opens the same answer drawer via `onOpen`.
 */

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import { Box, IconButton, InputBase, Skeleton, Switch, Tooltip, Typography } from '@mui/material';
import { type ReactNode, useEffect, useState } from 'react';

import {
  REASON_LABELS,
  type ChatOverview,
  type ChatQuestionItem,
  type ChatReviewItem,
  type QuestionFilter,
  type ChatTopicCount,
  type ChatUserUsage,
  type ReviewKind,
  useChatNotStarted,
  useChatQuestions,
  useChatReviewQueue,
  useMarkReviewed,
  useChatUsers,
} from '../../api/adminChat';
import {
  FONT,
  GOLD,
  GOLD_BUTTON,
  GOLD_DARK,
  GOLD_TINT,
  INK,
  LINE,
  MUTED,
  STATUS,
  SUBTLE,
  ago,
  card,
  eyebrow,
  initials,
  sectionTitle,
  when,
} from './askAbhiAdminTheme';

export type ActivityTab = 'questions' | 'people' | 'review' | 'topics';

/** A request from outside (a KPI tile) to show a particular view. */
export interface ActivityFocus {
  tab: ActivityTab;
  kind?: ReviewKind;
  unreviewed?: boolean;
  /** Changes on every request so the same view can be asked for twice. */
  nonce: number;
}

const PAGE_SIZE = 10;

const QUESTION_FILTERS: {
  value: QuestionFilter;
  label: string;
  count?: (o: ChatOverview) => number;
}[] = [
  { value: 'all', label: 'All' },
  { value: 'rated', label: 'Rated', count: (o) => o.thumbs_up + o.thumbs_down },
  { value: 'down', label: 'Thumbs Down', count: (o) => o.thumbs_down },
  { value: 'held_back', label: 'Held Back', count: (o) => o.withheld },
  { value: 'out_of_scope', label: 'Out of Scope', count: (o) => o.refused },
  {
    value: 'follow_ups',
    label: 'Follow-Ups',
    count: (o) => Math.max(0, o.questions - o.conversations),
  },
];

/** The review queue: everything that needs an admin's attention. */
const KINDS: { value: ReviewKind; label: string; help: string }[] = [
  {
    value: 'all',
    label: 'Needs Attention',
    help: 'Everything below: rated down, held back, errored, or passed only after a rewrite.',
  },
  {
    value: 'down',
    label: 'Thumbs Down',
    help: 'The person rated the answer bad — with the reasons and comment they gave.',
  },
  {
    value: 'withheld',
    label: 'Held Back',
    help: "A number in the draft didn't match the data even after two rewrites, so the person saw a 'couldn't verify' note instead of an answer.",
  },
  {
    value: 'error',
    label: 'Errors',
    help: 'The answer failed to generate (a server or Claude API problem). The person saw an error message.',
  },
  {
    value: 'rewritten',
    label: 'Passed After Rewrite',
    help: 'Shown to the person, but only after the number check sent the draft back. Borderline — an early warning before answers start getting held back.',
  },
];

function kindCount(o: ChatOverview, kind: ReviewKind): number | undefined {
  // "All" overlaps (a held-back answer can also be rated), so no count rather than a wrong one.
  if (kind === 'all') return undefined;
  if (kind === 'down') return o.thumbs_down;
  if (kind === 'up') return o.thumbs_up;
  if (kind === 'withheld') return o.withheld;
  if (kind === 'rewritten') return o.rewritten;
  if (kind === 'error') return o.errors;
  return o.thumbs_up + o.thumbs_down;
}

// ---------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------
export function AskAbhiActivity({
  days,
  overview,
  focus,
  onOpen,
}: {
  days: number;
  overview: ChatOverview | undefined;
  focus: ActivityFocus | null;
  onOpen: (answerId: number) => void;
}) {
  const [tab, setTab] = useState<ActivityTab>('questions');
  const [person, setPerson] = useState<{ id: string; name: string } | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [kind, setKind] = useState<ReviewKind>('all');
  const [unreviewedOnly, setUnreviewedOnly] = useState(true);
  const [qPage, setQPage] = useState(0);
  const [result, setResult] = useState<QuestionFilter>('all');
  const [subject, setSubject] = useState<Subject | null>(null);
  const mark = useMarkReviewed();
  const [rPage, setRPage] = useState(0);

  useEffect(() => {
    if (!focus) return;
    setTab(focus.tab);
    if (focus.kind) setKind(focus.kind);
    if (focus.unreviewed !== undefined) setUnreviewedOnly(focus.unreviewed);
  }, [focus]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => setQPage(0), [person, debounced, days, result, subject]);
  useEffect(() => setRPage(0), [person, kind, unreviewedOnly, days]);

  const users = useChatUsers(days);
  const asked = useChatQuestions({
    user_id: person?.id,
    search: debounced,
    result,
    topic: subject?.kind === 'topic' ? subject.key : undefined,
    state: subject?.kind === 'state' ? subject.key : undefined,
    product_id: subject?.kind === 'product' ? Number(subject.key) : undefined,
    days,
    limit: PAGE_SIZE,
    offset: qPage * PAGE_SIZE,
  });
  const queue = useChatReviewQueue({
    kind,
    reviewed: unreviewedOnly ? false : undefined,
    user_id: person?.id,
    days: Math.max(days, 90),
    limit: PAGE_SIZE,
    offset: rPage * PAGE_SIZE,
  });

  const toReview = overview?.needs_review ?? 0;
  const tabs: { value: ActivityTab; label: string; count?: number; alert?: boolean }[] = [
    { value: 'questions', label: 'Questions', count: overview?.questions },
    { value: 'people', label: 'People', count: users.data?.length },
    {
      value: 'review',
      label: 'Review',
      count: toReview || undefined,
      alert: (overview?.unreviewed_down ?? 0) > 0,
    },
    { value: 'topics', label: 'Topics' },
  ];

  return (
    <Box sx={{ ...card }}>
      {/* Header: title + segmented tabs */}
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          pt: 2.25,
          pb: 1.75,
          display: 'flex',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 1.5,
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography sx={eyebrow}>Activity</Typography>
          <Typography sx={sectionTitle}>What's Happening in Ask ABHI</Typography>
        </Box>
        <Box
          role="tablist"
          aria-label="Activity views"
          sx={{
            display: 'flex',
            gap: 0.25,
            p: 0.5,
            borderRadius: 999,
            bgcolor: 'rgba(15, 10, 7, 0.045)',
            // Phones: the four tabs share the full width instead of scrolling.
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {tabs.map((t) => {
            const active = t.value === tab;
            return (
              <Box
                key={t.value}
                component="button"
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.value)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: { xs: 1, sm: 'none' },
                  gap: { xs: 0.5, sm: 0.75 },
                  height: 32,
                  px: { xs: 0.75, sm: 1.5 },
                  borderRadius: 999,
                  fontFamily: FONT,
                  fontSize: { xs: 12.5, sm: 13 },
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: active ? INK : MUTED,
                  bgcolor: active ? '#fff' : 'transparent',
                  boxShadow: active ? '0 1px 3px rgba(15, 10, 7, 0.12)' : 'none',
                  transition: 'background-color 150ms ease, color 150ms ease',
                  '&:hover': { color: INK },
                  '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 1 },
                }}
              >
                {t.label}
                {t.count !== undefined && t.count > 0 && (
                  // Phones: only the Review count, so all four tabs fit.
                  <Box
                    component="span"
                    sx={{
                      display: {
                        xs: t.value === 'review' ? 'inline-flex' : 'none',
                        sm: 'inline-flex',
                      },
                    }}
                  >
                    <CountBadge alert={t.alert}>{t.count}</CountBadge>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Toolbar for the current tab */}
      {(tab === 'questions' || tab === 'review') && (
        <Box
          sx={{
            px: { xs: 2, md: 2.5 },
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          {tab === 'questions' && (
            <>
              <SearchBox value={search} onChange={setSearch} placeholder="Search questions" />
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {QUESTION_FILTERS.map((f) => (
                  <FilterChip
                    key={f.value}
                    active={result === f.value}
                    onClick={() => setResult(f.value)}
                    count={overview ? f.count?.(overview) : undefined}
                  >
                    {f.label}
                  </FilterChip>
                ))}
              </Box>
            </>
          )}
          {tab === 'review' && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {KINDS.map((k) => (
                <Tooltip key={k.value} title={k.help} placement="top" enterDelay={350}>
                  <span>
                    <FilterChip
                      active={kind === k.value}
                      onClick={() => setKind(k.value)}
                      count={overview ? kindCount(overview, k.value) : undefined}
                    >
                      {k.label}
                    </FilterChip>
                  </span>
                </Tooltip>
              ))}
              <Box
                aria-hidden
                sx={{ width: '1px', alignSelf: 'stretch', my: 0.5, mx: 0.5, bgcolor: LINE }}
              />
              <Tooltip title="Answers people liked — to browse what works. These don't need review.">
                <span>
                  <FilterChip
                    active={kind === 'up'}
                    onClick={() => setKind('up')}
                    count={overview?.thumbs_up}
                  >
                    Thumbs Up
                  </FilterChip>
                </span>
              </Tooltip>
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          {tab === 'questions' && subject && (
            <FilterChip active onClick={() => setSubject(null)} onClear={() => setSubject(null)}>
              {subject.label}
            </FilterChip>
          )}
          {person && (
            <FilterChip active onClick={() => setPerson(null)} onClear={() => setPerson(null)}>
              {person.name}
            </FilterChip>
          )}
          {tab === 'review' && <ReviewHelp />}
          {tab === 'review' && (
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
            >
              <Switch
                size="small"
                checked={unreviewedOnly}
                onChange={(e) => setUnreviewedOnly(e.target.checked)}
              />
              <Typography sx={{ fontSize: 13, color: MUTED }}>Not Reviewed Only</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Body */}
      {tab === 'questions' && (
        <>
          <QuestionsHeader />
          <RowList
            loading={asked.isLoading}
            empty={
              debounced ? `No questions mention “${debounced}”.` : 'No questions in this period.'
            }
            rows={asked.data?.items.map((q, i) => (
              <QuestionRow
                key={`${q.conversation_id}-${q.asked_at}-${i}`}
                q={q}
                onOpen={q.answer_id !== null ? () => onOpen(q.answer_id as number) : undefined}
              />
            ))}
          />
          <Pager page={qPage} total={asked.data?.total ?? 0} onPage={setQPage} noun="question" />
        </>
      )}

      {tab === 'people' && (
        <People
          days={days}
          rows={users.data}
          loading={users.isLoading}
          onPick={(u) => {
            setPerson({ id: u.user_id, name: u.name });
            setTab('questions');
          }}
        />
      )}

      {tab === 'review' && (
        <>
          <ReviewHeader />
          <RowList
            loading={queue.isLoading}
            empty={
              kind === 'all' || kind === 'down'
                ? unreviewedOnly
                  ? `All caught up — nothing ${kind === 'down' ? 'rated down ' : ''}waiting for review.`
                  : `No ${kind === 'down' ? 'thumbs-downs' : 'rated or failed answers'} in this period.`
                : 'Nothing here for this period.'
            }
            rows={queue.data?.items.map((r) => (
              <ReviewRow
                key={r.message_id}
                r={r}
                onOpen={() => onOpen(r.message_id)}
                busy={mark.isPending && mark.variables?.id === r.message_id}
                onMark={(reviewed) => mark.mutate({ id: r.message_id, reviewed })}
              />
            ))}
          />
          <Pager page={rPage} total={queue.data?.total ?? 0} onPage={setRPage} noun="answer" />
        </>
      )}

      {tab === 'topics' && (
        <Topics
          overview={overview}
          days={days}
          onPick={(next) => {
            setSubject(next);
            setResult('all');
            setTab('questions');
          }}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------
/** Right-hand columns, shared by the header and every row so they line up. */
const Q_COLUMNS = { xs: '1fr', md: 'minmax(0, 1fr) 84px 64px 72px 112px 20px' } as const;

function QuestionsHeader() {
  const h = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'rgba(15, 10, 7, 0.45)',
  } as const;
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'grid' },
        gridTemplateColumns: Q_COLUMNS.md,
        columnGap: 2,
        alignItems: 'center',
        px: 2.5,
        py: 0.9,
        bgcolor: 'rgba(187, 140, 63, 0.05)',
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Typography sx={{ ...h, pl: '44px' }}>Question</Typography>
      <Typography sx={h}>Feedback</Typography>
      <Typography sx={{ ...h, textAlign: 'right' }}>Time</Typography>
      <Typography sx={{ ...h, textAlign: 'right' }}>Cost</Typography>
      <Typography sx={h}>Result</Typography>
      <span />
    </Box>
  );
}

function secs(ms: number | null): string {
  if (ms === null) return '—';
  return `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`;
}

function usd(v: number | null): string {
  if (v === null) return '—';
  if (v > 0 && v < 0.01) return '<$0.01';
  return `$${v.toFixed(2)}`;
}

function QuestionRow({ q, onOpen }: { q: ChatQuestionItem; onOpen?: () => void }) {
  const tags = [...q.states, ...q.products];
  return (
    <Box
      role="listitem"
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (onOpen && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: Q_COLUMNS,
        columnGap: 2,
        rowGap: 1,
        alignItems: 'center',
        px: { xs: 2, md: 2.5 },
        py: 1.4,
        cursor: onOpen ? 'pointer' : 'default',
        transition: 'background-color 120ms ease',
        '&:hover': onOpen ? { bgcolor: 'rgba(187, 140, 63, 0.06)' } : undefined,
        '&:hover .row-go': { color: INK, transform: 'translateX(2px)' },
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: -2 },
      }}
    >
      {/* Question, who/when, and what it was about */}
      <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
        <Avatar name={q.user.name} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 550,
              color: INK,
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {q.question}
          </Typography>
          <Box
            sx={{
              mt: 0.35,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 0.75,
              rowGap: 0.5,
              fontSize: 12,
              color: SUBTLE,
            }}
          >
            <span>{q.user.name}</span>
            <Dot />
            <Tooltip title={when(q.asked_at)}>
              <span>{ago(q.asked_at)}</span>
            </Tooltip>
            {q.asked_times > 1 && (
              <Tooltip title="Asked this many times in the period (by anyone) — strong demand, or the answer didn't satisfy">
                <Box
                  component="span"
                  sx={{ ...pill(GOLD_DARK, GOLD_TINT), height: 18, px: 0.75, fontSize: 10.5 }}
                >
                  Asked {q.asked_times}×
                </Box>
              </Tooltip>
            )}
            {q.follow_up_number > 0 && (
              <Tooltip
                title={`Asked after ${q.follow_up_number} earlier question${q.follow_up_number === 1 ? '' : 's'} in the same chat`}
              >
                <Box
                  component="span"
                  sx={{
                    ...pill(MUTED, 'rgba(15, 10, 7, 0.05)'),
                    height: 18,
                    px: 0.75,
                    fontSize: 10.5,
                  }}
                >
                  ↳ Follow-Up {q.follow_up_number}
                </Box>
              </Tooltip>
            )}
          </Box>
          {(q.topics.length > 0 || tags.length > 0) && (
            <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {q.topics.map((t) => (
                <Box key={t} component="span" sx={topicPill}>
                  {t}
                </Box>
              ))}
              {tags.map((t) => (
                <Box key={t} component="span" sx={tagPill}>
                  {t}
                </Box>
              ))}
            </Box>
          )}
          {q.answer_preview && (
            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <Box
                component="img"
                src="/ask-abhi/logo-mark.svg"
                alt=""
                aria-hidden
                sx={{ width: 14, height: 14, mt: '2px', flexShrink: 0, opacity: 0.85 }}
              />
              <Typography
                sx={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: MUTED,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {q.answer_preview}
              </Typography>
            </Box>
          )}
          {q.comment && (
            <Typography
              sx={{
                mt: 0.75,
                pl: 1,
                borderLeft: '2px solid rgba(185, 28, 28, 0.35)',
                fontSize: 12.5,
                color: MUTED,
                fontStyle: 'italic',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              “{q.comment}”
            </Typography>
          )}
          {/* Phones and tablets: result, rating, time and cost on one line. */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
              fontSize: 12,
              color: SUBTLE,
            }}
          >
            {q.status ? <StatusChip status={q.status} /> : <Muted>No answer</Muted>}
            <Rating value={q.rating} />
            {q.rewrites > 0 && (
              <Box component="span" sx={{ fontWeight: 600, color: '#b45309' }}>
                {q.rewrites} rewrite{q.rewrites === 1 ? '' : 's'}
              </Box>
            )}
            <span>{secs(q.latency_ms)}</span>
            <span>
              {q.cost_estimated && q.cost_usd !== null ? '~' : ''}
              {usd(q.cost_usd)}
            </span>
          </Box>
        </Box>
      </Box>

      {/* Feedback */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
        }}
      >
        {q.rating === null ? (
          <Box component="span" sx={{ fontSize: 12, color: SUBTLE }}>
            No rating
          </Box>
        ) : (
          <>
            <Rating value={q.rating} />
            {q.reasons.length > 0 && (
              <Tooltip title={q.reasons.map((r) => REASON_LABELS[r] ?? r).join(' · ')}>
                <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: '#b91c1c' }}>
                  {q.reasons.length === 1
                    ? (REASON_LABELS[q.reasons[0] ?? ''] ?? q.reasons[0])
                    : `${q.reasons.length} reasons`}
                </Box>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      {/* Time / cost */}
      <Typography sx={{ ...numCell, display: { xs: 'none', md: 'block' } }}>
        {secs(q.latency_ms)}
      </Typography>
      <Tooltip
        title={
          q.cost_estimated ? 'Estimated from token counts (before exact costs were recorded)' : ''
        }
      >
        <Typography sx={{ ...numCell, display: { xs: 'none', md: 'block' } }}>
          {q.cost_estimated && q.cost_usd !== null ? '~' : ''}
          {usd(q.cost_usd)}
        </Typography>
      </Tooltip>

      {/* Result + review */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
        }}
      >
        {q.status ? <StatusChip status={q.status} /> : <Muted>No answer</Muted>}
        {q.rewrites > 0 && (
          <Tooltip title="The number check sent the draft back this many times before it passed or was held back">
            <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: '#b45309' }}>
              {q.rewrites} rewrite{q.rewrites === 1 ? '' : 's'}
            </Box>
          </Tooltip>
        )}
        {q.reviewed && (
          <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: '#047857' }}>
            ✓ Reviewed
          </Box>
        )}
      </Box>

      {onOpen ? (
        <ChevronRightIcon
          className="row-go"
          sx={{
            display: { xs: 'none', md: 'block' },
            fontSize: 18,
            color: SUBTLE,
            transition: 'transform 150ms ease, color 150ms ease',
          }}
        />
      ) : (
        <span />
      )}
    </Box>
  );
}

const numCell = {
  fontSize: 13,
  color: INK,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
} as const;

/** Question type: gold outline. */
const topicPill = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 20,
  px: 0.85,
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  color: GOLD_DARK,
  bgcolor: GOLD_TINT,
  whiteSpace: 'nowrap',
} as const;

/** State / product: neutral outline so it reads as a subject, not a type. */
const tagPill = {
  ...topicPill,
  color: MUTED,
  bgcolor: '#fff',
  border: `1px solid ${LINE}`,
} as const;

// ---------------------------------------------------------------------
// People
// ---------------------------------------------------------------------
const P_COLUMNS = 'minmax(0, 1.3fr) 150px minmax(0, 1.2fr) 130px 72px 76px 20px';

function People({
  days,
  rows,
  loading,
  onPick,
}: {
  days: number;
  rows: ChatUserUsage[] | undefined;
  loading: boolean;
  onPick: (u: ChatUserUsage) => void;
}) {
  const h = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'rgba(15, 10, 7, 0.45)',
  } as const;
  return (
    <>
      <Box
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: P_COLUMNS,
          columnGap: 2,
          alignItems: 'center',
          px: 2.5,
          py: 0.9,
          bgcolor: 'rgba(187, 140, 63, 0.05)',
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Typography sx={{ ...h, pl: '44px' }}>Person</Typography>
        <Typography sx={h}>Activity · {days}d</Typography>
        <Typography sx={h}>Asks about</Typography>
        <Typography sx={h}>Satisfaction</Typography>
        <Typography sx={{ ...h, textAlign: 'right' }}>Cost</Typography>
        <Typography sx={{ ...h, textAlign: 'right' }}>Active</Typography>
        <span />
      </Box>
      <RowList
        loading={loading}
        empty="Nobody has used Ask ABHI in this period."
        rows={rows?.map((u) => (
          <PersonRow key={u.user_id} u={u} onClick={() => onPick(u)} />
        ))}
      />
      <NotStarted />
    </>
  );
}

function PersonRow({ u, onClick }: { u: ChatUserUsage; onClick: () => void }) {
  const rated = u.thumbs_up + u.thumbs_down;
  // Asked about states outside their own territory? Worth knowing for reps.
  const outside = u.territory.length ? u.states_asked.filter((s) => !u.territory.includes(s)) : [];
  return (
    <Box
      role="listitem"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: P_COLUMNS },
        columnGap: 2,
        rowGap: 1.25,
        alignItems: 'center',
        px: { xs: 2, md: 2.5 },
        py: 1.5,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.06)' },
        '&:hover .row-go': { color: INK, transform: 'translateX(2px)' },
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: -2 },
      }}
    >
      {/* Person */}
      <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
        <Avatar name={u.name} />
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: INK }}>
            {u.name}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12, color: SUBTLE }}>
            {u.email}
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {u.roles.map((r) => (
              <RolePill key={r}>{r.replace(/_/g, ' ')}</RolePill>
            ))}
            {u.first_used && (
              <Typography component="span" sx={{ fontSize: 11, color: SUBTLE }}>
                since{' '}
                {new Date(u.first_used).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Activity */}
      <Box sx={{ pl: { xs: '44px', md: 0 } }}>
        <Sparkline values={u.daily} />
        <Typography sx={{ mt: 0.5, fontSize: 12, color: MUTED }}>
          <b style={{ color: INK }}>{u.questions}</b> questions · {u.active_days} active day
          {u.active_days === 1 ? '' : 's'}
        </Typography>
      </Box>

      {/* Asks about */}
      <Box sx={{ minWidth: 0, pl: { xs: '44px', md: 0 } }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {u.top_topics.slice(0, 2).map((t) => (
            <Box key={t.label} component="span" sx={topicPill}>
              {t.label}
            </Box>
          ))}
          {u.top_subjects.slice(0, 3).map((t) => (
            <Box key={t.label} component="span" sx={tagPill}>
              {t.label}
            </Box>
          ))}
        </Box>
        {u.territory.length > 0 && (
          <Typography noWrap sx={{ mt: 0.5, fontSize: 11.5, color: SUBTLE }}>
            Territory: {u.territory.join(', ')}
            {outside.length > 0 && (
              <Box component="span" sx={{ color: GOLD_DARK }}>
                {' '}
                · also asks about {outside.join(', ')}
              </Box>
            )}
          </Typography>
        )}
      </Box>

      {/* Satisfaction */}
      <Box sx={{ pl: { xs: '44px', md: 0 } }}>
        {rated ? (
          <>
            <Typography
              sx={{ fontSize: 15, fontWeight: 700, color: u.thumbs_down ? INK : '#047857' }}
            >
              {Math.round((u.thumbs_up / rated) * 100)}%
            </Typography>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: MUTED }}
            >
              <Stat value={u.thumbs_up} label={<ThumbUpAltOutlinedIcon sx={{ fontSize: 13 }} />} />
              <Stat
                value={u.thumbs_down}
                label={<ThumbDownAltOutlinedIcon sx={{ fontSize: 13 }} />}
                tone={u.thumbs_down ? '#b91c1c' : undefined}
              />
            </Box>
          </>
        ) : (
          <Typography sx={{ fontSize: 12, color: SUBTLE }}>No ratings</Typography>
        )}
        {(u.withheld > 0 || u.out_of_scope > 0) && (
          <Typography sx={{ mt: 0.25, fontSize: 11.5, color: '#b45309' }}>
            {[
              u.withheld ? `${u.withheld} held back` : '',
              u.out_of_scope ? `${u.out_of_scope} out of scope` : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        )}
        {/* Phones and tablets: the cost and last-active columns are hidden, so show them here. */}
        <Typography
          sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5, fontSize: 12, color: SUBTLE }}
        >
          {u.cost_estimated ? '~' : ''}${u.cost_usd.toFixed(2)} · active {ago(u.last_active)}
        </Typography>
      </Box>

      {/* Cost */}
      <Tooltip
        title={
          u.cost_estimated
            ? 'Includes estimates for answers from before exact costs were recorded'
            : ''
        }
      >
        <Typography
          sx={{
            display: { xs: 'none', md: 'block' },
            fontSize: 13,
            color: INK,
            textAlign: 'right',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {u.cost_estimated ? '~' : ''}${u.cost_usd.toFixed(2)}
        </Typography>
      </Tooltip>

      {/* Last active */}
      <Tooltip title={`Last active ${when(u.last_active)}`}>
        <Typography
          sx={{
            display: { xs: 'none', md: 'block' },
            fontSize: 12.5,
            color: MUTED,
            textAlign: 'right',
          }}
        >
          {ago(u.last_active)}
        </Typography>
      </Tooltip>

      <ChevronRightIcon
        className="row-go"
        sx={{
          display: { xs: 'none', md: 'block' },
          fontSize: 18,
          color: SUBTLE,
          transition: 'transform 150ms ease, color 150ms ease',
        }}
      />
    </Box>
  );
}

/** Tiny bar sparkline of questions per day — one hue, no axes; hover gives the numbers. */
function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(1, ...values);
  const total = values.reduce((a, b) => a + b, 0);
  const W = 140;
  const H = 24;
  const n = Math.max(values.length, 1);
  const slot = W / n;
  const bar = Math.max(1.5, slot - 1.5);
  return (
    <Tooltip title={`${total} question${total === 1 ? '' : 's'} · busiest day ${max}`}>
      <Box
        component="svg"
        role="img"
        aria-label={`Questions per day, ${total} in total`}
        viewBox={`0 0 ${W} ${H}`}
        sx={{ display: 'block', width: '100%', maxWidth: W, height: H }}
      >
        {values.map((v, i) => {
          const h = v ? Math.max(4, (v / max) * H) : 2;
          return (
            <rect
              key={i}
              x={i * slot}
              y={H - h}
              width={bar}
              height={h}
              rx={1}
              fill={v ? GOLD : 'rgba(187, 140, 63, 0.2)'}
            />
          );
        })}
      </Box>
    </Tooltip>
  );
}

/** People with access who haven't tried it — the adoption to-do list. */
function NotStarted() {
  const list = useChatNotStarted();
  const [copied, setCopied] = useState(false);
  const rows = list.data ?? [];
  if (!rows.length) return null;
  return (
    <Box sx={{ borderTop: `1px solid ${LINE}`, px: { xs: 2, md: 2.5 }, py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 650, color: INK }}>
            Haven't Tried Ask ABHI Yet <CountBadge>{rows.length}</CountBadge>
          </Typography>
          <Typography sx={{ fontSize: 12, color: SUBTLE }}>
            Active people whose role gives them access, but who haven't asked anything
          </Typography>
        </Box>
        <FilterChip
          active={false}
          onClick={() => {
            void navigator.clipboard.writeText(rows.map((r) => r.email).join(', ')).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
        >
          {copied ? '✓ Emails Copied' : 'Copy Emails'}
        </FilterChip>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 1,
        }}
      >
        {rows.map((r) => (
          <Box
            key={r.user_id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              p: 1.25,
              borderRadius: '10px',
              bgcolor: '#fff',
              border: `1px solid ${LINE}`,
              minWidth: 0,
            }}
          >
            <Avatar name={r.name} />
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: INK }}>
                {r.name}
              </Typography>
              <Typography noWrap sx={{ fontSize: 11.5, color: SUBTLE }}>
                {r.roles.map((x) => x.replace(/_/g, ' ')).join(', ')} ·{' '}
                {r.last_login_at ? `signed in ${ago(r.last_login_at)}` : 'never signed in'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------
// Topics
// ---------------------------------------------------------------------
/** What the question list can be narrowed to from the Topics tab. */
interface Subject {
  kind: 'topic' | 'state' | 'product';
  key: string;
  label: string;
}

function Topics({
  overview,
  days,
  onPick,
}: {
  overview: ChatOverview | undefined;
  days: number;
  onPick: (s: Subject) => void;
}) {
  if (!overview) return <Skeleton variant="rounded" height={180} sx={{ m: 2.5 }} />;
  const total = overview.topics.reduce((n, t) => n + t.count, 0);
  return (
    <Box
      sx={{
        p: { xs: 2, md: 2.5 },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' },
        gap: { xs: 3, lg: 4 },
      }}
    >
      {/* Question types */}
      <Box>
        <SubHead
          title="Question Types"
          sub={`Last ${days} days vs the ${days} before · click to see the questions`}
        />
        {overview.topics.length === 0 ? (
          <Muted>No questions in this period.</Muted>
        ) : (
          <Box sx={{ ...innerCard }}>
            <Box
              sx={{
                display: { xs: 'none', sm: 'grid' },
                gridTemplateColumns: TOPIC_COLUMNS,
                columnGap: 1.5,
                px: 1.5,
                py: 0.75,
                borderBottom: `1px solid ${LINE}`,
                '& > *': {
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: 'rgba(15, 10, 7, 0.45)',
                },
              }}
            >
              <span>Type</span>
              <span style={{ textAlign: 'right' }}>Questions</span>
              <span style={{ textAlign: 'right' }}>Trend</span>
              <span>Feedback</span>
            </Box>
            {overview.topics.map((t) => (
              <TopicRow
                key={t.key ?? t.label}
                t={t}
                share={total ? t.count / total : 0}
                onClick={() => t.key && onPick({ kind: 'topic', key: t.key, label: t.label })}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Subjects + unmet demand */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <SubHead title="States Asked About Most" />
          <SubjectList
            items={overview.top_states}
            onPick={(t) => t.key && onPick({ kind: 'state', key: t.key, label: t.label })}
          />
        </Box>
        <Box>
          <SubHead title="Products Asked About Most" />
          <SubjectList
            items={overview.top_products}
            onPick={(t) => t.key && onPick({ kind: 'product', key: t.key, label: t.label })}
          />
        </Box>
        <Box>
          <SubHead
            title="Asked, but Out of Scope"
            sub="What people wanted that Ask ABHI doesn't cover yet"
          />
          {overview.out_of_scope_examples.length === 0 ? (
            <Muted>Nothing turned down in this period.</Muted>
          ) : (
            <Box sx={{ ...innerCard, p: 0.5 }}>
              {overview.out_of_scope_examples.map((q) => (
                <Box
                  key={q}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    px: 1.25,
                    py: 0.9,
                    '& + &': { borderTop: `1px solid ${LINE}` },
                  }}
                >
                  <Box component="span" sx={{ color: SUBTLE, fontSize: 13, lineHeight: 1.5 }}>
                    “
                  </Box>
                  <Typography sx={{ fontSize: 13, color: INK, lineHeight: 1.5 }}>{q}</Typography>
                </Box>
              ))}
              <Box sx={{ px: 1.25, py: 0.9, borderTop: `1px solid ${LINE}` }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() =>
                    onPick({ kind: 'topic', key: 'status:refused', label: 'Out of Scope' })
                  }
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: GOLD_DARK,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  See All Out-of-Scope Questions →
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const TOPIC_COLUMNS = 'minmax(0, 1fr) 92px 64px 120px';

const innerCard = {
  bgcolor: '#fff',
  border: `1px solid ${LINE}`,
  borderRadius: '10px',
  overflow: 'hidden',
} as const;

function SubHead({ title, sub }: { title: string; sub?: string }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 650, color: INK }}>{title}</Typography>
      {sub && <Typography sx={{ fontSize: 12, color: SUBTLE }}>{sub}</Typography>}
    </Box>
  );
}

/** ▲ 3 / ▼ 2 / New / — against the previous period. */
function Trend({ now, before }: { now: number; before: number | null }) {
  if (before === null) return <Muted>—</Muted>;
  if (before === 0) {
    return (
      <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: GOLD_DARK }}>
        New
      </Box>
    );
  }
  const diff = now - before;
  if (diff === 0)
    return (
      <Box component="span" sx={{ fontSize: 12, color: SUBTLE }}>
        —
      </Box>
    );
  return (
    <Tooltip title={`${before} in the previous period`}>
      <Box
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: diff > 0 ? '#047857' : '#b91c1c',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {diff > 0 ? '▲' : '▼'} {Math.abs(diff)}
      </Box>
    </Tooltip>
  );
}

function TopicRow({
  t,
  share,
  onClick,
}: {
  t: ChatTopicCount;
  share: number;
  onClick: () => void;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr auto', sm: TOPIC_COLUMNS },
        columnGap: 1.5,
        rowGap: 0.5,
        alignItems: 'center',
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '& + &': { borderTop: `1px solid ${LINE}` },
        '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.06)' },
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: -2 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13, fontWeight: 550, color: INK }}>
          {t.label}
        </Typography>
        <Box sx={{ mt: 0.5, height: 4, borderRadius: 999, bgcolor: 'rgba(187, 140, 63, 0.12)' }}>
          <Box
            sx={{
              height: '100%',
              width: `${share * 100}%`,
              borderRadius: 999,
              background: GOLD_BUTTON,
            }}
          />
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 650,
          color: INK,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {t.count}
        <Box component="span" sx={{ ml: 0.75, fontSize: 11.5, fontWeight: 500, color: SUBTLE }}>
          {Math.round(share * 100)}%
        </Box>
      </Typography>
      <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
        <Trend now={t.count} before={t.previous} />
      </Box>
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, fontSize: 12 }}>
        {t.thumbs_up > 0 && (
          <Stat value={t.thumbs_up} label={<ThumbUpAltOutlinedIcon sx={{ fontSize: 13 }} />} />
        )}
        {t.thumbs_down > 0 && (
          <Stat
            value={t.thumbs_down}
            label={<ThumbDownAltOutlinedIcon sx={{ fontSize: 13 }} />}
            tone="#b91c1c"
          />
        )}
        {t.held_back > 0 && <Stat value={t.held_back} label="held" tone="#b45309" />}
        {!t.thumbs_up && !t.thumbs_down && !t.held_back && <Muted>—</Muted>}
      </Box>
    </Box>
  );
}

function SubjectList({
  items,
  onPick,
}: {
  items: ChatTopicCount[];
  onPick: (t: ChatTopicCount) => void;
}) {
  if (!items.length) return <Muted>None yet.</Muted>;
  const max = Math.max(...items.map((i) => i.count));
  return (
    <Box sx={{ ...innerCard }}>
      {items.map((i, idx) => (
        <Box
          key={i.key ?? i.label}
          role="button"
          tabIndex={0}
          onClick={() => onPick(i)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onPick(i);
            }
          }}
          sx={{
            display: 'grid',
            gridTemplateColumns: '18px minmax(0, 1fr) auto auto',
            columnGap: 1.25,
            alignItems: 'center',
            px: 1.5,
            py: 0.9,
            cursor: 'pointer',
            '& + &': { borderTop: `1px solid ${LINE}` },
            '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.06)' },
            '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: -2 },
          }}
        >
          <Typography sx={{ fontSize: 11, color: SUBTLE, fontVariantNumeric: 'tabular-nums' }}>
            {idx + 1}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, color: INK }}>
              {i.label}
            </Typography>
            <Box
              sx={{ mt: 0.5, height: 4, borderRadius: 999, bgcolor: 'rgba(187, 140, 63, 0.12)' }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${(i.count / max) * 100}%`,
                  borderRadius: 999,
                  background: GOLD_BUTTON,
                }}
              />
            </Box>
          </Box>
          <Trend now={i.count} before={i.previous} />
          <Typography
            sx={{
              minWidth: 20,
              textAlign: 'right',
              fontSize: 13,
              fontWeight: 650,
              color: INK,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {i.count}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------
/** "How review works" — what lands here, how it gets here, and what to do with it. */
function ReviewHelp() {
  const item = (title: string, body: string) => (
    <Box component="li" sx={{ '& + &': { mt: 0.75 } }}>
      <Box component="span" sx={{ fontWeight: 650, color: INK }}>
        {title}
      </Box>{' '}
      — {body}
    </Box>
  );
  return (
    <Tooltip
      placement="bottom-end"
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      slotProps={{
        tooltip: {
          sx: { maxWidth: 380, p: 1.75, borderRadius: '12px' },
        },
      }}
      title={
        <Box sx={{ fontSize: 12.5, lineHeight: 1.5, color: MUTED }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK, mb: 0.75 }}>
            How Review Works
          </Typography>
          <Typography sx={{ fontSize: 12.5, mb: 0.75 }}>
            Answers land here automatically when one of these happens:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {item('Thumbs Down', 'the person rated the answer bad, with any reasons or comment.')}
            {item(
              'Held Back',
              "a number didn't match the data after two rewrites, so no answer was shown.",
            )}
            {item('Error', 'the answer failed to generate.')}
            {item(
              'Passed After Rewrite',
              'the number check sent the draft back before it was shown — borderline.',
            )}
          </Box>
          <Typography sx={{ fontSize: 12.5, mt: 1 }}>
            Thumbs-ups don't need review; browse them with the Thumbs Up chip.
          </Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.75 }}>
            Open a row to see the question, the answer, the data it used and the number check. Then{' '}
            <b>Mark Reviewed</b> (add a note in the panel if you like) — it leaves this list while{' '}
            <i>Not Reviewed Only</i> is on. Items stay until someone reviews them.
          </Typography>
        </Box>
      }
    >
      <Box
        component="button"
        type="button"
        aria-label="How Review Works"
        sx={{
          all: 'unset',
          cursor: 'help',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          height: 30,
          px: 1,
          borderRadius: 999,
          fontSize: 12.5,
          fontWeight: 600,
          color: MUTED,
          '&:hover, &:focus-visible': { color: INK, bgcolor: 'rgba(15, 10, 7, 0.04)' },
          '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 1 },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        How Review Works
      </Box>
    </Tooltip>
  );
}

const R_COLUMNS = 'minmax(0, 1fr) 150px 190px 128px 20px';

function ReviewHeader() {
  const h = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'rgba(15, 10, 7, 0.45)',
  } as const;
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'grid' },
        gridTemplateColumns: R_COLUMNS,
        columnGap: 2,
        px: 2.5,
        py: 0.9,
        bgcolor: 'rgba(187, 140, 63, 0.05)',
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Typography sx={{ ...h, pl: '44px' }}>Question & answer</Typography>
      <Typography sx={h}>Their feedback</Typography>
      <Typography sx={h}>Why it's here</Typography>
      <Typography sx={h}>Review</Typography>
      <span />
    </Box>
  );
}

function ReviewRow({
  r,
  onOpen,
  onMark,
  busy,
}: {
  r: ChatReviewItem;
  onOpen: () => void;
  onMark: (reviewed: boolean) => void;
  busy: boolean;
}) {
  const needsAttention =
    r.rating === -1 || r.status === 'unverified' || r.status === 'error' || r.rewrites > 0;
  const problems: string[] = [];
  if (r.rating === -1) problems.push('Rated down by the user');
  if (r.unmatched_numbers.length) {
    problems.push(
      `${r.unmatched_numbers
        .map((n) => (r.nearest[n] ? `${n} (closest ${r.nearest[n]})` : n))
        .join(', ')} didn't match the data`,
    );
  }
  if (r.status === 'error') problems.push('The answer failed to generate');
  if (r.status === 'refused') problems.push('Out of Scope');
  return (
    <Box
      role="listitem"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen();
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: R_COLUMNS },
        columnGap: 2,
        rowGap: 1,
        alignItems: 'center',
        px: { xs: 2, md: 2.5 },
        py: 1.4,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.06)' },
        '&:hover .row-go': { color: INK, transform: 'translateX(2px)' },
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: -2 },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
        <Avatar name={r.user.name} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 550, color: INK, lineHeight: 1.4 }}>
            {r.question ?? '—'}
          </Typography>
          <Box
            sx={{
              mt: 0.35,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 0.75,
              rowGap: 0.5,
              fontSize: 12,
              color: SUBTLE,
            }}
          >
            <span>{r.user.name}</span>
            <Dot />
            <Tooltip title={when(r.feedback_at ?? r.created_at)}>
              <span>{ago(r.feedback_at ?? r.created_at)}</span>
            </Tooltip>
            {r.topics.map((t) => (
              <Box key={t} component="span" sx={topicPill}>
                {t}
              </Box>
            ))}
          </Box>
          {r.answer_preview && (
            <Box sx={{ mt: 0.75, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <Box
                component="img"
                src="/ask-abhi/logo-mark.svg"
                alt=""
                aria-hidden
                sx={{ width: 14, height: 14, mt: '2px', flexShrink: 0, opacity: 0.85 }}
              />
              <Typography
                sx={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: MUTED,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {r.answer_preview}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Their feedback */}
      <Box sx={{ pl: { xs: '44px', md: 0 }, minWidth: 0 }}>
        {r.rating === null ? (
          <Muted>Not rated</Muted>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Rating value={r.rating} />
            {r.reasons.map((x) => (
              <ReasonPill key={x}>{REASON_LABELS[x] ?? x}</ReasonPill>
            ))}
          </Box>
        )}
        {r.comment && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 12,
              color: MUTED,
              fontStyle: 'italic',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            “{r.comment}”
          </Typography>
        )}
      </Box>

      {/* Why it's here */}
      <Box sx={{ pl: { xs: '44px', md: 0 }, minWidth: 0 }}>
        <StatusChip status={r.status} />
        {problems.map((p) => (
          <Typography key={p} sx={{ mt: 0.5, fontSize: 12, color: '#b45309', lineHeight: 1.4 }}>
            {p}
          </Typography>
        ))}
        {r.rewrites > 0 && (
          <Typography
            sx={{ mt: 0.25, fontSize: 11.5, color: r.status === 'unverified' ? SUBTLE : '#b45309' }}
          >
            {r.status === 'unverified' ? '' : 'Passed after '}
            {r.rewrites} rewrite{r.rewrites === 1 ? '' : 's'}
            {r.status === 'unverified' ? ' by the number check' : ' — borderline'}
          </Typography>
        )}
        {!needsAttention && r.rating === 1 && (
          <Typography sx={{ mt: 0.5, fontSize: 12, color: '#047857' }}>Rated up</Typography>
        )}
      </Box>

      {/* Review — only for answers that need attention */}
      <Box sx={{ pl: { xs: '44px', md: 0 } }} onClick={(e) => e.stopPropagation()}>
        {!needsAttention ? (
          <Muted>No review needed</Muted>
        ) : r.reviewed_at ? (
          <Box>
            <Tooltip title={r.review_note || ''}>
              <Box component="span" sx={pill('#047857', '#ecfdf5')}>
                ✓ {r.reviewed_by?.split(' ')[0] ?? 'Reviewed'}
              </Box>
            </Tooltip>
            <Box
              component="button"
              type="button"
              disabled={busy}
              onClick={() => onMark(false)}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                display: 'block',
                mt: 0.5,
                fontSize: 11.5,
                color: SUBTLE,
                '&:hover': { color: INK },
              }}
            >
              Undo
            </Box>
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            disabled={busy}
            onClick={() => onMark(true)}
            sx={{
              all: 'unset',
              cursor: busy ? 'default' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              height: 28,
              px: 1.25,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              color: INK,
              background: GOLD_BUTTON,
              opacity: busy ? 0.6 : 1,
              '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
            }}
          >
            {busy ? 'Saving…' : 'Mark Reviewed'}
          </Box>
        )}
      </Box>

      <ChevronRightIcon
        className="row-go"
        sx={{
          display: { xs: 'none', md: 'block' },
          fontSize: 18,
          color: SUBTLE,
          transition: 'transform 150ms ease, color 150ms ease',
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------
function RowList({
  rows,
  loading,
  empty,
}: {
  rows: ReactNode[] | undefined;
  loading: boolean;
  empty: string;
}) {
  if (loading) {
    return (
      <Box sx={{ px: 2.5, py: 1.5 }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1.25 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="55%" />
              <Skeleton width="30%" />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  if (!rows?.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Muted>{empty}</Muted>
      </Box>
    );
  }
  return (
    <Box role="list" sx={{ '& > * + *': { borderTop: `1px solid ${LINE}` } }}>
      {rows}
    </Box>
  );
}

function Avatar({ name }: { name: string }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.02em',
        color: GOLD_DARK,
        bgcolor: GOLD_TINT,
        border: '1px solid rgba(187, 140, 63, 0.25)',
      }}
    >
      {initials(name)}
    </Box>
  );
}

function Pager({
  page,
  total,
  onPage,
  noun,
}: {
  page: number;
  total: number;
  onPage: (p: number) => void;
  noun: string;
}) {
  if (total <= PAGE_SIZE) {
    return total > 0 ? (
      <Box sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${LINE}` }}>
        <Muted>
          {total} {noun}
          {total === 1 ? '' : 's'}
        </Muted>
      </Box>
    ) : null;
  }
  const from = page * PAGE_SIZE + 1;
  const to = Math.min(total, from + PAGE_SIZE - 1);
  const last = Math.ceil(total / PAGE_SIZE) - 1;
  return (
    <Box
      sx={{
        px: 2.5,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        borderTop: `1px solid ${LINE}`,
      }}
    >
      <Muted>
        {from}–{to} of {total} {noun}s
      </Muted>
      <Box sx={{ flex: 1 }} />
      <IconButton
        size="small"
        aria-label="Previous page"
        disabled={page === 0}
        onClick={() => onPage(page - 1)}
      >
        <ChevronRightIcon sx={{ fontSize: 18, transform: 'rotate(180deg)' }} />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Next page"
        disabled={page >= last}
        onClick={() => onPage(page + 1)}
      >
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        height: 34,
        px: 1.25,
        width: { xs: '100%', sm: 280 },
        borderRadius: 999,
        bgcolor: '#fff',
        border: `1px solid ${LINE}`,
        '&:focus-within': {
          borderColor: 'rgba(187, 140, 63, 0.55)',
          boxShadow: `0 0 0 3px ${GOLD_TINT}`,
        },
      }}
    >
      <SearchIcon sx={{ fontSize: 17, color: SUBTLE }} />
      <InputBase
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputProps={{ 'aria-label': placeholder }}
        sx={{ flex: 1, fontSize: 13, fontFamily: FONT }}
      />
      {value && (
        <IconButton
          size="small"
          aria-label="Clear search"
          onClick={() => onChange('')}
          sx={{ p: 0.25 }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Box>
  );
}

function FilterChip({
  active,
  onClick,
  onClear,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  onClear?: () => void;
  count?: number;
  children: ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={active}
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        height: 30,
        px: 1.25,
        borderRadius: 999,
        fontFamily: FONT,
        fontSize: 12.5,
        fontWeight: 600,
        transition: 'background-color 140ms ease, border-color 140ms ease',
        '&:focus-visible': { outline: `2px solid ${GOLD}`, outlineOffset: 2 },
        ...(active
          ? { background: GOLD_BUTTON, color: INK, border: '1px solid transparent' }
          : {
              bgcolor: '#fff',
              color: MUTED,
              border: `1px solid ${LINE}`,
              '&:hover': { borderColor: 'rgba(187, 140, 63, 0.4)', color: INK },
            }),
      }}
    >
      {children}
      {count !== undefined && count > 0 && (
        <Box
          component="span"
          sx={{ fontSize: 11, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}
        >
          {count}
        </Box>
      )}
      {onClear && <CloseIcon sx={{ fontSize: 14, ml: 0.25 }} />}
    </Box>
  );
}

function CountBadge({ alert, children }: { alert?: boolean; children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        minWidth: 18,
        height: 18,
        px: 0.6,
        borderRadius: 999,
        display: 'inline-grid',
        placeItems: 'center',
        verticalAlign: 'middle',
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        bgcolor: alert ? 'rgba(185, 28, 28, 0.1)' : GOLD_TINT,
        color: alert ? '#b91c1c' : GOLD_DARK,
      }}
    >
      {children}
    </Box>
  );
}

function pill(color: string, bg: string) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: 22,
    px: 1,
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 600,
    color,
    bgcolor: bg,
    whiteSpace: 'nowrap',
  } as const;
}

export function StatusChip({ status }: { status: string | null }) {
  const s = STATUS[status ?? ''] ?? {
    label: status ?? '—',
    color: MUTED,
    bg: 'rgba(15,10,7,0.05)',
  };
  return (
    <Box component="span" sx={pill(s.color, s.bg)}>
      {s.label}
    </Box>
  );
}

function RolePill({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        ...pill(GOLD_DARK, GOLD_TINT),
        height: 18,
        px: 0.75,
        fontSize: 10.5,
        textTransform: 'capitalize',
      }}
    >
      {children}
    </Box>
  );
}

function ReasonPill({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{ ...pill('#b91c1c', '#fef2f2'), height: 18, px: 0.75, fontSize: 10.5 }}
    >
      {children}
    </Box>
  );
}

function Rating({ value }: { value: number | null }) {
  if (value === 1)
    return (
      <Tooltip title="Thumbs up">
        <ThumbUpAltOutlinedIcon sx={{ fontSize: 16, color: '#047857' }} />
      </Tooltip>
    );
  if (value === -1)
    return (
      <Tooltip title="Thumbs down">
        <ThumbDownAltOutlinedIcon sx={{ fontSize: 16, color: '#b91c1c' }} />
      </Tooltip>
    );
  return null;
}

function Stat({
  value,
  label,
  strong,
  tone,
}: {
  value: number;
  label: ReactNode;
  strong?: boolean;
  tone?: string;
}) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 12.5,
        color: tone ?? (value ? MUTED : SUBTLE),
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: strong ? 700 : 600,
          color: tone ?? (strong ? INK : value ? INK : SUBTLE),
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Box>
      {label}
    </Box>
  );
}

function Dot() {
  return (
    <Box component="span" aria-hidden sx={{ color: SUBTLE }}>
      ·
    </Box>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <Typography sx={{ fontSize: 13, color: SUBTLE }}>{children}</Typography>;
}
